//Global imports
import { cilExternalLink } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardFooter,
    CCardText,
    CCardTitle,
    CCol,
    CLink,
} from '@coreui/react';

//File imports
import { Language } from '../../../../language/English';
import { DraftPojo } from '../../Api/DraftPojo';
import {
    APP_LANGAUGE,
    LANGAUGE_NAME,
    WEBSITE_BASE_URL,
    USER_CREATED_DOCUMENT_TYPE_DOCUMENT_TEMPLATE_ONLY,
} from '../../../../Constant';
import DateUtility from '../../../../services/DateUtils/DateUtils';
import { UtmParametersModal } from '../../../../components';
import { useState } from 'react';

const DraftCard = (props: { draft: DraftPojo; key: number }) => {
    const data = props.draft;
    let metaInfo = data?.metaInfo ?? '{}';
    let isUtmAvailable =
        JSON.parse(metaInfo)?.utm &&
        Object.keys(JSON.parse(metaInfo)?.utm).some((key) =>
            key.endsWith('_campaignid')
        );
    const dateUtils = new DateUtility();

    const [utmModalVisible, setUtmModalVisible] = useState(false);

    const getLangaugeBasedOnLangaugeId = (langaugeId: number): string => {
        const langKey = Object.keys(APP_LANGAUGE).find(
            (key) => APP_LANGAUGE[key] === langaugeId,
        );

        return langKey ? LANGAUGE_NAME[langKey] : '';
    };

    return (
        <CCol xs={12} md={4} className="mb-4">
            <CCard className="border-0 shadow-sm rounded-2">
                <CCardBody>
                    <div className="d-flex justify-content-between align-items-start">
                        <CCardTitle
                            className="fs-3 text-truncate"
                            style={{ maxWidth: '100%' }}
                        >
                            {data?.translation?.title}
                        </CCardTitle>
                    </div>
                    <div className="d-flex justify-content-between fst-italic">
                        {dateUtils.formatUnixTimestampToDateString(
                            data?.createdOn,
                        )}
                    </div>
                    <div className="d-flex justify-content-between">
                        <CCardText className="mb-0">
                            {Language.LANGAUGE}:{' '}
                            <span className="fw-bold">
                                {getLangaugeBasedOnLangaugeId(
                                    data?.translation?.languageId,
                                )}
                            </span>
                        </CCardText>
                        <CCardText className="mb-0">
                            {Language.PRICE}:{' '}
                            <span className="fw-bold">
                                {data?.template?.price}
                            </span>
                        </CCardText>
                    </div>

                    {JSON.parse(metaInfo)?.referer && (
                        <div className="d-flex justify-content-between mt-1">
                            <CCardText className="mb-0 text-break">
                                {Language.REFERRER}:{' '}
                                <span className="fw-bold">
                                    {JSON.parse(metaInfo)?.referer}
                                </span>
                            </CCardText>
                        </div>
                    )}

                    <div className="d-flex justify-content-between mt-1">
                        <CCardText className="mb-0">
                            {Language.GOOGLE_ADS}:{' '}
                            <span className="fw-bold">
                                {isUtmAvailable ? Language.YES : Language.NO}
                            </span>
                            {isUtmAvailable && (
                                <CLink
                                    href="#"
                                    className="link-primary small text-decoration-underline mx-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setUtmModalVisible(true);
                                    }}
                                >
                                    {Language.VIEW}
                                </CLink>
                            )}
                        </CCardText>
                    </div>

                    <div className="d-flex justify-content-between mt-1">
                        <CCardText className="mb-0">
                            {Language.PAYMENT_INITIATED}:{' '}
                            <span className={`fw-bold text-uppercase ${data?.paymentId ? 'text-success' : 'text-danger'}`}>
                                {data?.paymentId ? Language.YES : Language.NO}
                            </span>
                        </CCardText>
                    </div>

                    <div className="d-flex justify-content-between mt-1">
                        <CCardText className="mb-0">
                            <small>{Language.DOCUMENT_TYPE}: </small>
                            <small className="fw-bold">
                                {JSON.parse(data.userRequestPayload ?? '{}').documentType === USER_CREATED_DOCUMENT_TYPE_DOCUMENT_TEMPLATE_ONLY
                                    ? Language.DOCUMENT_TEMPLATE_ONLY
                                    : Language.FILLED_DOCUMENT}
                            </small>
                        </CCardText>
                    </div>

                    {(() => {
                        if (!data?.creationIpInfo) return null;
                        try {
                            const ipInfo = typeof data.creationIpInfo === 'string' 
                                ? JSON.parse(data.creationIpInfo) 
                                : data.creationIpInfo;
                            let addressComponents = [ipInfo?.cityName ?? ipInfo?.region_name, ipInfo?.countryName ?? ipInfo?.country_name]
                            const locationStr = addressComponents.filter(Boolean).join(', ');
                            if (locationStr) {
                                return (
                                    <div className="d-flex justify-content-between mt-1">
                                        <CCardText className="mb-0">
                                            <small>{Language.CUSTOMER_LOCATION}: </small>
                                            <small className="fw-bold">{locationStr}</small>
                                        </CCardText>
                                    </div>
                                );
                            }
                        } catch (e) {
                            console.error("Error parsing creationIpInfo", e);
                        }
                        return null;
                    })()}

                    {data?.creationIp && (
                        <div className="d-flex justify-content-between mt-1 mb-2">
                            <CCardText className="mb-0">
                                <small>{Language.IP_ADDRESS}: </small>
                                <small className="fw-bold">{data.creationIp}</small>
                            </CCardText>
                        </div>
                    )}

                    <CCardText className="fw-bold mt-1 mb-1">
                        {Language.CUSTOMER}
                    </CCardText>
                    <hr className="my-2" />

                    {data?.customer?.name && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.name}</span>
                        </CCardText>
                    )}

                    {data?.customer?.email && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.email}</span>
                        </CCardText>
                    )}

                    {data?.customer?.phoneNumber && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.phoneNumber}</span>
                        </CCardText>
                    )}
                </CCardBody>
                <CCardFooter className="bg-white">
                    <div className="d-flex gap-2">
                        {JSON.parse(data.userRequestPayload ?? '{}').documentType !==
                            USER_CREATED_DOCUMENT_TYPE_DOCUMENT_TEMPLATE_ONLY && (
                            <CButton
                                color="primary"
                                size="sm"
                                onClick={() =>
                                    window.open(
                                        `${WEBSITE_BASE_URL}/${data?.translation?.createDocumentUrl}/${data?.uniqueId}`,
                                        '_blank',
                                    )
                                }
                            >
                                <CIcon icon={cilExternalLink} className="me-2" />
                                {Language.SEE_DOCUMENT}
                            </CButton>
                        )}
                    </div>
                </CCardFooter>
            </CCard>

            <UtmParametersModal
                visible={utmModalVisible}
                onClose={() => setUtmModalVisible(false)}
                utmParams={JSON.parse(metaInfo)?.utm ?? {}}
            />
        </CCol>
    );
};

export default DraftCard;
